/**
 * "Book a consultation" dialog state.
 *
 * Every consultation CTA on the site — the nav's Book a call, the hero, the
 * service rail buttons, the sponsor tiers, the page footers — opens the same
 * dialog through this context, passing a `source` so we know which one it was.
 *
 * Lives in its own module (not ElfShell) so the header can consume it without
 * an import cycle.
 */

import React, { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface ConsultContext {
  /** Which CTA opened it, e.g. `nav`, `hero`, `services.marketing`. */
  source: string;
  /** Pre-selects the interest picker when the CTA implies a service. */
  interest?: string;
  /** Overrides the dialog heading for CTAs that aren't "book a consultation". */
  title?: string;
  /** Pre-fills the email field for CTAs that captured it inline (waitlists). */
  email?: string;
}

interface ConsultValue {
  open: (ctx: ConsultContext) => void;
  close: () => void;
  context: ConsultContext | null;
}

const Ctx = createContext<ConsultValue | null>(null);

export const ConsultProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [context, setContext] = useState<ConsultContext | null>(null);

  const open = useCallback((next: ConsultContext) => setContext(next), []);
  const close = useCallback(() => setContext(null), []);

  const value = useMemo<ConsultValue>(() => ({ open, close, context }), [open, close, context]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

/** Returns null outside the provider so a CTA can render standalone. */
export function useConsult(): ConsultValue | null {
  return useContext(Ctx);
}

/**
 * Handy for CTAs: returns a click handler that opens the dialog, or undefined
 * when there's no provider (so the button simply does nothing rather than
 * throwing).
 */
export function useConsultOpener(ctx: ConsultContext) {
  const consult = useConsult();
  return useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      consult?.open(ctx);
    },
    // ctx is a literal at every call site; spread so it isn't a new dep object
    [consult, ctx.source, ctx.interest, ctx.title, ctx.email],
  );
}
